/**
 * トグル機能を管理
 *
 * 仕様
 * - ist-js__toggle: 親
 * - ist-js__toggle--autoClose: クリックアウトサイドでの閉じる機能を有効にする
 * - ist-js__toggleButton: ボタン
 * - ist-js__toggleContent: コンテンツ
 *
 * 使い方
 * <div class="ist-js__toggle ist-js__toggle--autoClose">
 *   <button class="ist-js__toggleButton">開閉ボタン1</button>
 *   <div class="ist-js__toggleContent">
 *     コンテンツ1
 *   </div>
 * </div>
 *
 * 追加仕様01
 * - ist-js__toggle--notAutoFocus: フォーカスをコンテンツに移動しない
 */

/**
 * トグル機能を管理
 */
export class Toggle {
  private container: Element;
  private button: HTMLElement | null;
  private content: HTMLElement | null;
  private boundHandleClickOutside: (event: MouseEvent) => void;
  private boundHandleFocusOut: (event: FocusEvent) => void;
  private isAutoClose: boolean;
  private notAutoFocus: boolean; // 追加仕様01

  constructor(container: Element) {
    this.container = container;
    this.button = container.querySelector('.ist-js__toggleButton');
    this.content = container.querySelector('.ist-js__toggleContent');
    this.isAutoClose = container.classList.contains(
      'ist-js__toggle--autoClose'
    );
    this.notAutoFocus = container.classList.contains(
      'ist-js__toggle--notAutoFocus'
    ); // 追加仕様01

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleFocusOut = this.handleFocusOut.bind(this);

    this.init();
  }

  private init(): void {
    if (!this.button || !this.content) return;

    if (!this.content.id) {
      this.content.id = `toggle-content-${Math.random().toString(36).substr(2, 9)}`;
    }

    this.button.setAttribute('aria-controls', this.content.id);
    this.button.setAttribute('aria-expanded', 'false');

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleContent();
    });

    if (this.isAutoClose) {
      document.addEventListener('click', this.boundHandleClickOutside);
      document.addEventListener('focusin', this.boundHandleFocusOut);
    }
  }

  private handleClickOutside(event: MouseEvent): void {
    if (!this.isAutoClose) return;

    const target = event.target as Node;

    // クリックされた要素がボタンまたはコンテンツ内なら何もしない
    if (
      (this.button && this.button.contains(target)) ||
      (this.content && this.content.contains(target))
    ) {
      return;
    }

    // それ以外なら閉じる
    if (this.isOpen()) {
      this.close();
    }
  }

  private handleFocusOut(event: FocusEvent): void {
    if (!this.isAutoClose) return;

    const target = event.target as Node;

    // フォーカスが移った要素がボタンまたはコンテンツ内なら何もしない
    if (
      (this.button && this.button.contains(target)) ||
      (this.content && this.content.contains(target))
    ) {
      return;
    }

    // それ以外なら閉じる
    if (this.isOpen()) {
      this.close();
    }
  }

  private toggleContent(): void {
    if (!this.content || !this.button) return;

    const isOpen = this.isOpen();

    if (!isOpen) {
      this.open();

      if (!this.notAutoFocus) {
        // 追加仕様01: フォーカス移動を抑制
        setTimeout(() => {
          const focusableElements = this.content?.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements && focusableElements.length > 0) {
            (focusableElements[0] as HTMLElement).focus();
          } else {
            this.content?.setAttribute('tabindex', '-1');
            this.content?.focus();
          }
        }, 10);
      }
    } else {
      this.close();
      this.button.focus();
    }
  }

  public isOpen(): boolean {
    return this.content?.classList.contains('ist-js__toggleOpen') || false;
  }

  public open(): void {
    if (!this.content || !this.button) return;

    this.content.classList.add('ist-js__toggleOpen');
    this.button.classList.add(
      'ist-js__toggleButton--animate',
      'ist-js__toggleButton--open'
    );
    this.button.setAttribute('aria-expanded', 'true');
  }

  public close(): void {
    if (!this.content || !this.button) return;

    this.content.classList.remove('ist-js__toggleOpen');
    this.button.classList.remove(
      'ist-js__toggleButton--animate',
      'ist-js__toggleButton--open'
    );
    this.button.setAttribute('aria-expanded', 'false');
  }

  public destroy(): void {
    if (this.isAutoClose) {
      document.removeEventListener('click', this.boundHandleClickOutside);
      document.removeEventListener('focusin', this.boundHandleFocusOut);
    }
  }
}

/**
 * 複数のトグルを管理するマネージャークラス
 */
export class ToggleManager {
  private toggles: Toggle[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeToggles();
      });
    } else {
      this.initializeToggles();
    }
  }

  private initializeToggles(): void {
    const containers = document.querySelectorAll('.ist-js__toggle');
    containers.forEach((container) => {
      this.toggles.push(new Toggle(container));
    });
  }

  public getToggles(): Toggle[] {
    return this.toggles;
  }

  public openAll(): void {
    this.toggles.forEach((toggle) => toggle.open());
  }

  public closeAll(): void {
    this.toggles.forEach((toggle) => toggle.close());
  }

  public getOpenToggles(): Toggle[] {
    return this.toggles.filter((toggle) => toggle.isOpen());
  }

  public destroy(): void {
    this.toggles.forEach((toggle) => toggle.destroy());
    this.toggles = [];
  }
}
